import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import * as fromGenerated from '../../_generated';
import {
  defaultChatLockKey,
  readApiErrorName,
} from '../../chats/assistants/default-chat-lock.helper';
import { AdminUsersActions } from './admin-users.actions';

@Injectable()
export class AdminUsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUsersActions.loadUsers),
      switchMap(() =>
        // timer(1000) ensures the spinner is visible for at least 1s to avoid a jarring flash on fast connections.
        forkJoin([
          this.adminService.adminControllerGetUsersV1(),
          timer(1000),
        ]).pipe(
          map(([users]) => AdminUsersActions.loadUsersSuccess({ users })),
          catchError((error: Error) =>
            of(AdminUsersActions.loadUsersFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  loadUsersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminUsersActions.loadUsersFailure),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant(
              'ADMIN.USERS.TABLE.LOAD_USERS_ERROR',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  // Lazily fetch a user's expand payload (teammates + groups) when a count-cell
  // dropdown opens. mergeMap so multiple rows can resolve concurrently; a
  // failure leaves that row's dropdown empty.
  loadDropdownPayload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUsersActions.loadDropdownPayload),
      mergeMap(({ userId }) =>
        this.adminService
          .adminControllerGetUserDropdownPayloadV1(userId, this.currentLocale())
          .pipe(
            map((payload) =>
              AdminUsersActions.loadDropdownPayloadSuccess({ userId, payload }),
            ),
            catchError((error: Error) =>
              of(
                AdminUsersActions.loadDropdownPayloadFailure({
                  userId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUsersActions.updateUserRole),
      switchMap(({ userId, role }) =>
        this.adminService
          .adminControllerUpdateUserRoleV1(userId, { role })
          .pipe(
            map((user) => AdminUsersActions.updateUserRoleSuccess({ user })),
            catchError((error: Error) =>
              of(
                AdminUsersActions.updateUserRoleFailure({
                  error: error.message,
                  errorName: readApiErrorName(error) ?? null,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateUserRoleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminUsersActions.updateUserRoleSuccess),
        tap(() => {
          this.toastrService.success(
            this.translateService.instant(
              'ADMIN.USERS.TABLE.UPDATE_ROLE_SUCCESS',
            ) as string,
            undefined,
            { closeButton: true },
          );
        }),
      ),
    { dispatch: false },
  );

  updateUserRoleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminUsersActions.updateUserRoleFailure),
        tap(({ errorName }) => {
          this.toastrService.error(
            this.translateService.instant(
              defaultChatLockKey(errorName) ??
                'ADMIN.USERS.TABLE.UPDATE_ROLE_ERROR',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Admin reads resolve localized teammate/skill names server-side, so every
   * request that accepts a locale gets the viewer's current UI language.
   */
  private currentLocale(): 'en' | 'fr' {
    // The app registers only en + fr; narrow at runtime (default en) rather than
    // casting, so an unexpected lang can't reach the API as an invalid locale.
    return this.translateService.getCurrentLang() === 'fr' ? 'fr' : 'en';
  }
}
